import { useEffect, useRef, useState } from 'preact/hooks';
import {
  ApiError,
  getReviewPortalContext,
  fetchPortalTape,
  submitReviewPortal,
  type ReviewPortalContext,
  type ReviewMarker,
} from '../lib/api';
import { PortalFrame } from '../components/PortalFrame';
import './ReviewerPortal.css';

// B7 — the EXTERNAL, unauthenticated reviewer portal (the "send-to-coach" page).
// Rendered standalone by App.tsx BEFORE the auth/tenant gate, for a visitor with
// NO session and NO tenant: it's reached by the hash route #/portal/review/:token
// (the bare /portal/review/:token path is a registered SERVER JSON route, so the
// human-facing link is the hash form that loads this SPA, which then fetches the
// JSON itself). Every call is authorized SOLELY by the token.
//
// It: (a) fetches the reviewer's minimal scoped context, (b) plays the granted
// tape through the object-URL pattern (token-scoped, NO X-Tenant-Id), (c) lets
// the reviewer type written notes AND record audio via MediaRecorder while the
// tape plays — capturing time_markers = the tape's currentTime at record-start
// and at each "mark" tap — then submits. A bad/expired/used token and a browser
// without MediaRecorder are both handled calmly, never a crash.

export interface ReviewerPortalProps {
  token: string;
  /** The DEPLOYMENT's product name, from the pack's entry config. `null`
   * while it is still being fetched — never a guessed name on a stranger's
   * screen. See PortalFrame's `brand`. */
  brand?: string | null;
}

type Phase =
  | { kind: 'loading' }
  | { kind: 'invalid' } // token gone/expired/revoked/used — uniform graceful message
  | { kind: 'already' } // this review was already submitted
  | { kind: 'ready'; context: ReviewPortalContext }
  | { kind: 'done'; transcriptFilled: boolean };

// Burst safety (spec v2, decision 2743ecba): the tape door can answer 409 with a NAMED refusal instead of the tape.
// Each is shown in the player's place, in the server's own sentence. Only the two that CAN change on their own are
// re-checked, quietly, with no button: `being_prepared` (every upload is made ready to share once, minutes at most)
// every 15 s, and `pending_scan` (the hourly re-scan releases it) every minute. `quarantined` and
// `cannot_share_format` are permanent: no re-check, no retry.
export const TAPE_RECHECK_MS: Record<string, number> = { being_prepared: 15_000, pending_scan: 60_000 };
const TAPE_REFUSAL_FALLBACK: Record<string, string> = {
  being_prepared:
    'This tape is being made ready to share, a step every new upload goes through. It will play here as soon as that finishes. Nothing has been lost.',
  pending_scan: 'This tape is waiting on a security scan. It will play as soon as the scan finishes — nothing has been lost.',
  quarantined: 'This tape cannot be shared. The family has been told.',
  cannot_share_format:
    "This tape is in a video format that can't be prepared for sharing, so it can't play here. The person who sent it can share it again as an MP4 or MOV.",
};

const ROLE_LABEL: Record<string, string> = {
  coach: 'their coach',
  agent: 'their agent',
  manager: 'their manager',
  self: 'themselves',
  other: 'a reviewer',
};

// Best-effort mm:ss for a marker chip.
function clock(t: number): string {
  const total = Math.max(0, Math.round(t));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function mediaRecorderSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof (window as { MediaRecorder?: unknown }).MediaRecorder !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === 'function'
  );
}

export function ReviewerPortal({ token, brand }: ReviewerPortalProps) {
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const context = await getReviewPortalContext(token);
        if (cancelled) return;
        if (!context) {
          setPhase({ kind: 'invalid' });
          return;
        }
        setPhase(context.alreadySubmitted ? { kind: 'already' } : { kind: 'ready', context });
      } catch {
        if (!cancelled) setPhase({ kind: 'invalid' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Fix tranche 6, item 3 (walk S3.26): a hash-route SPA never remounts, and
  // never fires ANY DOM event (confirmed against a real Chromium: no reload,
  // no 'hashchange', no 'pageshow'), when the SAME already-open tab is sent
  // back to the identical URL it is already sitting on — which is exactly
  // what "click this link again" does when the coach's mail client (or the
  // walk's own re-navigation) lands on a tab that never went away. The
  // server has always known the truth the moment it happened (the grant's
  // `used_at` and the review's `received_at`, both set atomically at submit)
  // — this phase's copy just never asked again. Re-validate the one time a
  // person could plausibly be looking at this tab again: when it regains
  // visibility after having gone to the back of a phone/desktop, which is
  // the real-world shape of "reopening a link" a stale in-memory `done`
  // cannot outlive. Scoped to `done` ONLY — 'ready' holds a reviewer's
  // unsaved typed notes and recorded audio, which a background refetch must
  // never risk clobbering.
  useEffect(() => {
    if (phase.kind !== 'done') return;
    let cancelled = false;
    const revalidate = () => {
      if (document.visibilityState !== 'visible') return;
      void getReviewPortalContext(token)
        .then((context) => {
          if (cancelled || !context) return;
          // Always true once 'done' was ever reached — the point is not the
          // condition, it is that the copy now reflects the server's own
          // answer instead of the moment-of-submit receipt going stale.
          if (context.alreadySubmitted) setPhase({ kind: 'already' });
        })
        .catch(() => undefined); // a failed re-check leaves the receipt as-is
    };
    document.addEventListener('visibilitychange', revalidate);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', revalidate);
    };
  }, [phase.kind, token]);

  if (phase.kind === 'loading') {
    return (
      <div class="reviewer-surface">
        <PortalFrame brand={brand}>{<div class="portal__soft">One moment…</div>}</PortalFrame>
      </div>
    );
  }
  if (phase.kind === 'invalid') {
    return (
      <div class="reviewer-surface">
        <PortalFrame brand={brand}>
          <div class="portal__lead">This link is no longer active.</div>
          <div class="portal__soft">It may have expired, been used, or been turned off. Ask for a fresh link.</div>
        </PortalFrame>
      </div>
    );
  }
  if (phase.kind === 'already') {
    return (
      <div class="reviewer-surface">
        <PortalFrame brand={brand}>
          <div class="portal__lead">Your notes are in — thank you.</div>
          <div class="portal__soft">This tape has already had your feedback. Nothing more to do.</div>
        </PortalFrame>
      </div>
    );
  }
  if (phase.kind === 'done') {
    return (
      <div class="reviewer-surface">
        <PortalFrame brand={brand}>
          <div class="portal__lead">Thank you — your notes are on their way.</div>
          <div class="portal__soft">
            {phase.transcriptFilled ? 'Your recording is being transcribed for them.' : 'They’ll see them shortly.'}
          </div>
        </PortalFrame>
      </div>
    );
  }

  return (
    <ReviewerForm brand={brand}
      token={token}
      context={phase.context}
      onSubmitted={(transcriptFilled) => setPhase({ kind: 'done', transcriptFilled })}
      onConsumed={() => setPhase({ kind: 'already' })}
      onInvalid={() => setPhase({ kind: 'invalid' })}
    />
  );
}

interface ReviewerFormProps {
  /** The deployment's product name — see PortalFrame's `brand`. */
  brand?: string | null;
  token: string;
  context: ReviewPortalContext;
  onSubmitted: (transcriptFilled: boolean) => void;
  onConsumed: () => void;
  onInvalid: () => void;
}

function ReviewerForm({ token, context, onSubmitted, onConsumed, onInvalid, brand }: ReviewerFormProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [tapeUrl, setTapeUrl] = useState<string | null>(null);
  const [tapeFailed, setTapeFailed] = useState(false);
  const [tapeRefusal, setTapeRefusal] = useState<{ code: string; message: string } | null>(null);
  // Bumped by the quiet re-check timer; each bump re-runs the tape fetch below.
  const [tapeAttempt, setTapeAttempt] = useState(0);
  const [body, setBody] = useState('');
  const [markers, setMarkers] = useState<ReviewMarker[]>([]);
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const canRecord = mediaRecorderSupported();

  // Load the tape's bytes (token-scoped, header-free) as an object URL.
  useEffect(() => {
    if (!context.tapePresent) return;
    let cancelled = false;
    let revoke: string | null = null;
    let recheck: ReturnType<typeof setTimeout> | null = null;
    void fetchPortalTape(token)
      .then((file) => {
        if (cancelled) {
          URL.revokeObjectURL(file.objectUrl);
          return;
        }
        revoke = file.objectUrl;
        setTapeRefusal(null);
        setTapeUrl(file.objectUrl);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 409 && TAPE_REFUSAL_FALLBACK[err.code]) {
          setTapeRefusal({ code: err.code, message: err.serverMessage ?? TAPE_REFUSAL_FALLBACK[err.code]! });
          const wait = TAPE_RECHECK_MS[err.code];
          if (wait) recheck = setTimeout(() => setTapeAttempt((n) => n + 1), wait);
          return;
        }
        setTapeFailed(true);
      });
    return () => {
      cancelled = true;
      if (recheck) clearTimeout(recheck);
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [token, context.tapePresent, tapeAttempt]);

  // Revoke the recorded-audio preview URL + stop any live mic on unmount.
  useEffect(() => {
    return () => {
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, [audioUrl]);

  function tapeTime(): number {
    const t = videoRef.current?.currentTime;
    return typeof t === 'number' && Number.isFinite(t) ? t : 0;
  }

  async function startRecording() {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        setAudioBlob(blob);
        if (audioUrl) URL.revokeObjectURL(audioUrl);
        setAudioUrl(URL.createObjectURL(blob));
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      };
      recorderRef.current = recorder;
      recorder.start();
      // Mark where in the tape the recording began, and start it playing.
      setMarkers((prev) => [...prev, { t: tapeTime(), label: 'recording started' }]);
      void videoRef.current?.play().catch(() => {});
      setRecording(true);
    } catch {
      setError('Microphone permission was declined — you can still leave written notes.');
    }
  }

  function stopRecording() {
    recorderRef.current?.stop();
    recorderRef.current = null;
    setRecording(false);
    videoRef.current?.pause();
  }

  function markMoment() {
    setMarkers((prev) => [...prev, { t: tapeTime() }]);
  }

  function clearRecording() {
    setAudioBlob(null);
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioUrl(null);
    setMarkers((prev) => prev.filter((m) => m.label === undefined)); // keep manual marks, drop the auto start
  }

  async function submit(e: Event) {
    e.preventDefault();
    setError(null);
    const trimmed = body.trim();
    if (!trimmed && !audioBlob) {
      setError('Add a written note or a recording before sending.');
      return;
    }
    setSaving(true);
    try {
      const result = await submitReviewPortal(token, {
        body: trimmed || undefined,
        audio: audioBlob ?? undefined,
        timeMarkers: markers.length > 0 ? markers : undefined,
      });
      if (result.outcome === 'submitted') onSubmitted(result.transcriptFilled);
      else if (result.outcome === 'consumed') onConsumed();
      else onInvalid();
    } catch (err) {
      // The server's own sentence when it gave one. "Please try again" only
      // where trying again can work — a 4xx refusal repeated is refused
      // identically, and inviting the retry blames the reviewer for it.
      if (err instanceof ApiError) {
        setError(err.status >= 500 ? `${err.message} Please try again.` : err.message);
      } else {
        setError('Could not reach the server — your notes were not sent. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  }

  const role = context.reviewerRole ? ROLE_LABEL[context.reviewerRole] ?? 'a reviewer' : null;
  const roleWord = context.reviewerRole && context.reviewerRole !== 'self' && context.reviewerRole !== 'other'
    ? context.reviewerRole
    : null;
  const project = context.audition?.project ?? null;
  const auditionRole = context.audition?.role ?? null;

  // The household's own name wins over the deployment's when Pro's
  // de-branding is on — the server decides which, and sends `brand: null` when
  // it is not. See ReviewPortalContext.brand.
  return (
    <div class="reviewer-surface">
      <PortalFrame brand={context.brand ?? brand}>
        <div class="portal__lead">
          {context.reviewerName ? `Hi ${context.reviewerName} —` : 'Notes on this tape'}
        </div>
        <div class="portal__soft">
          {roleWord ? `You’ve been asked, as ${role}, to leave notes on this self-tape` : 'Leave your notes on this self-tape'}
          {project ? ` for ${project}` : ''}
          {auditionRole ? ` (${auditionRole})` : ''}. They watch and read them back later — audio records as you play,
          synced to the tape.
        </div>

        {context.tapePresent ? (
          tapeUrl ? (
            // eslint-disable-next-line jsx-a11y/media-has-caption -- a self-tape has
            // no caption track; the surrounding copy names it.
            <video ref={videoRef} class="portal__tape" src={tapeUrl} controls preload="metadata" playsInline />
          ) : tapeRefusal ? (
            // The server's own sentence, in the player's place. No button: the page re-checks on its own where
            // the answer can change, and a permanent refusal offers nothing to press.
            <div class="portal__tape-fail" role="status" data-testid="tape-refusal" data-code={tapeRefusal.code}>
              {tapeRefusal.message}
            </div>
          ) : tapeFailed ? (
            <div class="portal__tape-fail">This tape couldn’t be loaded. You can still leave written notes below.</div>
          ) : (
            <div class="portal__tape portal__tape--loading" aria-hidden="true" />
          )
        ) : (
          <div class="portal__soft" data-testid="no-tape">No tape was attached — written notes are welcome.</div>
        )}

        <form class="portal__form" onSubmit={submit}>
          <label class="portal__label" for="portal-notes">
            Written notes
          </label>
          <textarea
            id="portal-notes"
            class="portal__textarea"
            value={body}
            placeholder="What landed, what to try next time…"
            rows={5}
            onInput={(e) => setBody((e.target as HTMLTextAreaElement).value)}
          />

          <div class="portal__rec">
            {canRecord ? (
              <>
                {!recording && !audioBlob && (
                  <button type="button" class="portal__rec-btn" onClick={() => void startRecording()}>
                    <span aria-hidden="true">●</span> Record audio note
                  </button>
                )}
                {recording && (
                  <div class="portal__rec-live">
                    <button type="button" class="portal__rec-btn portal__rec-btn--stop" onClick={stopRecording}>
                      <span aria-hidden="true">■</span> Stop
                    </button>
                    <button type="button" class="portal__mark-btn" onClick={markMoment}>
                      Mark this moment
                    </button>
                    <span class="portal__rec-dot" aria-hidden="true" />
                    <span class="portal__rec-note">Recording · playing the tape</span>
                  </div>
                )}
                {audioBlob && !recording && audioUrl && (
                  <div class="portal__rec-done">
                    {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                    <audio class="portal__rec-audio" src={audioUrl} controls preload="metadata" aria-label="Your recording" />
                    <button type="button" class="portal__rec-clear" onClick={clearRecording}>
                      Re-record
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div class="portal__soft" data-testid="no-recorder">
                Audio recording isn’t available in this browser — written notes work perfectly.
              </div>
            )}
          </div>

          {markers.length > 0 && (
            <div class="portal__markers" data-testid="markers">
              Marked: {markers.map((m, i) => (
                <span class="portal__marker" key={i}>
                  {clock(m.t)}
                </span>
              ))}
            </div>
          )}

          {error && <div class="portal__error">{error}</div>}

          <button type="submit" class="portal__send" disabled={saving}>
            {saving ? 'Sending…' : 'Send my notes'}
          </button>
        </form>
      </PortalFrame>
    </div>
  );
}
